import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarHeart, Eye, EyeOff, Gift, PartyPopper, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { SeasonCard } from "@/components/SeasonalLayer";
import {
  SEASON_EVENTS, SEASON_CATEGORIES, SEASON_EFFECTS, SEASON_TIMEZONES, DEFAULT_SEASON, CUSTOM_EVENT_ID,
  defaultAutoEvents, resolveSeason, upcomingSeasons, nextOccurrence, findSeasonEvent, formatSeasonDate,
} from "@/lib/seasons";

// Panel admin > Site > Événements : met tout le site aux couleurs d'une fête
// (Noël, Halloween, Nouvel An…), avec compte à rebours et effets animés.

const MODES = [
  { id: "off", label: "Désactivé", hint: "Le site reste normal." },
  { id: "auto", label: "Automatique", hint: "Le site suit le calendrier : chaque fête cochée s'affiche toute seule quelques jours avant." },
  { id: "manual", label: "Choisir un événement", hint: "Un seul événement, affiché tout le temps jusqu'à ce que tu changes." },
];

const EMPTY_CUSTOM = { name: "", emoji: "🎉", date: "", yearly: false, before: 7, span: 1, label: "", live: "", color: "#6366f1", color2: "#a855f7", effect: "confetti" };

function Toggle({ on, onChange, label, hint, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="text-white/40 text-xs mt-0.5">{hint}</p>}
      </div>
      <button
        type="button"
        data-testid={testId}
        onClick={() => onChange(!on)}
        className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${on ? "bg-blue-500" : "bg-white/10"}`}
        aria-pressed={on}
      >
        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 left-0 transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

const shortDate = (ts, tz) => {
  const opts = { day: "numeric", month: "short", year: "numeric" };
  if (tz && tz !== "visitor") opts.timeZone = tz;
  try { return new Intl.DateTimeFormat("fr-FR", opts).format(new Date(ts)); } catch { return ""; }
};

export default function SeasonPanel({ hdr }) {
  const [saved, setSaved] = useState(DEFAULT_SEASON);
  const [draft, setDraft] = useState(DEFAULT_SEASON);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    axios.get(`${API}/admin/site-settings`, { headers: hdr() })
      .then((r) => {
        const season = { ...DEFAULT_SEASON, ...(r.data?.season || {}) };
        setSaved(season);
        setDraft(season);
      })
      .catch(() => toast.error("Impossible de charger les réglages"))
      .finally(() => setLoaded(true));
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(id);
      window.dispatchEvent(new CustomEvent("vp-season-preview", { detail: null }));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const custom = draft.custom || EMPTY_CUSTOM;
  const setCustom = (patch) => set({ custom: { ...custom, ...patch } });
  const customReady = Boolean(custom.name && custom.date);
  const autoList = Array.isArray(draft.auto_events) ? draft.auto_events : defaultAutoEvents();
  const autoSet = new Set(autoList);
  const toggleAuto = (id) => set({ auto_events: autoSet.has(id) ? autoList.filter((x) => x !== id) : [...autoList, id] });

  // Ce qu'on enverrait au serveur : l'événement personnalisé seulement s'il est rempli.
  const payload = useMemo(() => {
    const out = { ...draft, custom: customReady ? { ...custom, before: +custom.before || 0, span: Math.max(1, +custom.span || 1) } : null };
    if (out.mode === "manual" && out.event === CUSTOM_EVENT_ID && !out.custom) out.event = "noel";
    return out;
  }, [draft, custom, customReady]);

  const minute = Math.floor(now / 60000);
  const live = useMemo(() => resolveSeason(saved, minute * 60000), [saved, minute]);
  const preview = useMemo(() => resolveSeason(payload, now), [payload, now]);
  const upcoming = useMemo(() => upcomingSeasons(payload, minute * 60000, 12), [payload, minute]);
  const dirty = JSON.stringify(payload) !== JSON.stringify({ ...saved, custom: saved.custom || null });

  useEffect(() => {
    if (previewing) window.dispatchEvent(new CustomEvent("vp-season-preview", { detail: payload }));
  }, [previewing, payload]);

  const startPreview = () => {
    setPreviewing(true);
    window.dispatchEvent(new CustomEvent("vp-season-preview", { detail: payload }));
    toast.success("Aperçu affiché sur cette page (non enregistré)");
  };
  const stopPreview = () => {
    setPreviewing(false);
    window.dispatchEvent(new CustomEvent("vp-season-preview", { detail: null }));
  };
  const testCelebration = () => {
    if (!previewing) startPreview();
    setTimeout(() => window.dispatchEvent(new CustomEvent("vp-season-celebrate")), 50);
  };

  const testEgg = () => {
    if (!previewing) startPreview();
    setTimeout(() => window.dispatchEvent(new CustomEvent("vp-season-egg")), 80);
  };

  const save = async () => {
    if (draft.mode === "manual" && draft.event === CUSTOM_EVENT_ID && !customReady) {
      toast.error("Donne un nom et une date à l'événement personnalisé");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/site-settings`, { season: payload }, { headers: hdr() });
      const season = { ...DEFAULT_SEASON, ...(r.data?.settings?.season || payload) };
      setSaved(season);
      setDraft(season);
      stopPreview();
      toast.success(payload.mode === "off" ? "Événements désactivés" : "Enregistré : tout le site est à jour d'ici 30 secondes");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Réglages refusés : vérifie les champs");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="text-white/40 text-sm">Chargement…</p>;

  const tz = draft.timezone;
  const eventChoices = SEASON_CATEGORIES.map((cat) => ({ cat, events: SEASON_EVENTS.filter((e) => e.cat === cat.id) }));

  return (
    <div data-testid="season-panel">
      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><PartyPopper className="w-5 h-5 text-pink-400" />Événements & fêtes</h2>
      <p className="text-white/40 text-sm mb-5">Met tout le site aux couleurs d'une fête : Noël, Halloween, Nouvel An, Saint-Valentin, Octobre Rose… avec un compte à rebours en direct et des effets animés. Les visiteurs le voient d'ici 30 secondes après l'enregistrement.</p>

      <div className={`rounded-lg border p-4 mb-6 text-sm ${live ? "border-pink-500/30 bg-pink-500/[0.06]" : "border-white/5 bg-[#12121a]"}`}>
        {live ? (
          <span>En ce moment sur le site : <b>{live.emoji} {live.title}</b> — {live.phase === "countdown" ? `compte à rebours jusqu'au ${shortDate(live.target, saved.timezone)}` : "la fête est en cours"}.</span>
        ) : (
          <span className="text-white/60">En ce moment : aucun événement affiché{saved.mode === "auto" ? " (aucune fête cochée n'est proche)" : ""}.</span>
        )}
      </div>

      <div className="grid xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <div className="space-y-6 min-w-0">
          {/* Mode */}
          <div className="bg-[#12121a] border border-white/5 rounded-lg p-5">
            <h3 className="font-semibold mb-3">Mode</h3>
            <div className="grid sm:grid-cols-3 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`season-mode-${m.id}`}
                  onClick={() => set({ mode: m.id })}
                  className={`text-left rounded-lg border p-3 transition-colors ${draft.mode === m.id ? "border-blue-500/60 bg-blue-500/10" : "border-white/10 hover:border-white/20"}`}
                >
                  <p className="text-sm font-medium">{m.label}</p>
                  <p className="text-white/40 text-xs mt-1 leading-snug">{m.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Choix de l'événement */}
          {draft.mode === "manual" && (
            <div className="bg-[#12121a] border border-white/5 rounded-lg p-5">
              <h3 className="font-semibold mb-1">Événement affiché</h3>
              <p className="text-white/40 text-xs mb-4">Le compte à rebours vise la prochaine date de la fête (l'an prochain si elle est passée).</p>
              <div className="space-y-4">
                {eventChoices.map(({ cat, events }) => (
                  <div key={cat.id}>
                    <p className="text-[11px] uppercase tracking-wider text-white/35 mb-2">{cat.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {events.map((ev) => {
                        const occ = nextOccurrence(ev, minute * 60000, tz);
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            data-testid={`season-event-${ev.id}`}
                            onClick={() => set({ event: ev.id })}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${draft.event === ev.id ? "border-blue-500/60 bg-blue-500/15 text-white" : "border-white/10 text-white/75 hover:border-white/25"}`}
                            title={occ ? formatSeasonDate(occ.start, tz) : ""}
                          >
                            <span>{ev.emoji}</span>{ev.name}
                            {occ && <span className="text-white/35 text-xs">{shortDate(occ.start, tz)}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-white/35 mb-2">Le tien</p>
                  <button
                    type="button"
                    data-testid="season-event-custom"
                    onClick={() => set({ event: CUSTOM_EVENT_ID })}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${draft.event === CUSTOM_EVENT_ID ? "border-blue-500/60 bg-blue-500/15" : "border-white/10 text-white/75 hover:border-white/25"}`}
                  >
                    <span>{custom.emoji || "🎉"}</span>{custom.name || "Événement personnalisé"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Calendrier automatique */}
          {draft.mode === "auto" && (
            <div className="bg-[#12121a] border border-white/5 rounded-lg p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <h3 className="font-semibold">Fêtes du calendrier</h3>
                <div className="flex gap-1.5">
                  <Button type="button" variant="ghost" className="h-7 text-xs" onClick={() => set({ auto_events: null })}><RotateCcw className="w-3 h-3 mr-1" />Par défaut</Button>
                  <Button type="button" variant="ghost" className="h-7 text-xs" onClick={() => set({ auto_events: [...SEASON_EVENTS.map((e) => e.id), ...(customReady ? [CUSTOM_EVENT_ID] : [])] })}>Tout</Button>
                  <Button type="button" variant="ghost" className="h-7 text-xs" onClick={() => set({ auto_events: [] })}>Aucune</Button>
                </div>
              </div>
              <p className="text-white/40 text-xs mb-4">Chaque fête cochée s'affiche toute seule quelques jours avant (compte à rebours), puis le jour J. Si deux tombent en même temps, la plus importante passe devant.</p>
              <div className="space-y-4">
                {eventChoices.map(({ cat, events }) => (
                  <div key={cat.id}>
                    <p className="text-[11px] uppercase tracking-wider text-white/35 mb-2">{cat.label}</p>
                    <div className="grid sm:grid-cols-2 gap-1.5">
                      {events.map((ev) => {
                        const occ = nextOccurrence(ev, minute * 60000, tz);
                        return (
                          <label key={ev.id} className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 hover:bg-white/[0.03] cursor-pointer text-sm">
                            <input type="checkbox" checked={autoSet.has(ev.id)} onChange={() => toggleAuto(ev.id)} className="accent-blue-500 w-4 h-4" data-testid={`season-auto-${ev.id}`} />
                            <span>{ev.emoji}</span>
                            <span className="flex-1 min-w-0 truncate">{ev.name}</span>
                            {occ && <span className="text-white/30 text-xs shrink-0">{shortDate(occ.start, tz)}</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {customReady && (
                  <label className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 hover:bg-white/[0.03] cursor-pointer text-sm">
                    <input type="checkbox" checked={autoSet.has(CUSTOM_EVENT_ID)} onChange={() => toggleAuto(CUSTOM_EVENT_ID)} className="accent-blue-500 w-4 h-4" />
                    <span>{custom.emoji}</span><span>{custom.name} (personnalisé)</span>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Événement personnalisé */}
          <details className="bg-[#12121a] border border-white/5 rounded-lg p-5 group" open={draft.event === CUSTOM_EVENT_ID && draft.mode === "manual"}>
            <summary className="font-semibold cursor-pointer select-none list-none flex items-center justify-between">
              <span>Événement personnalisé <span className="text-white/40 font-normal text-sm">(anniversaire du site, sortie d'un produit, tournoi…)</span></span>
              <span className="text-white/40 text-xs group-open:hidden">Ouvrir</span>
            </summary>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="sm:col-span-2 grid grid-cols-[80px_1fr] gap-3">
                <div>
                  <Label className="text-white/60 text-xs">Emoji</Label>
                  <Input value={custom.emoji} maxLength={16} onChange={(e) => setCustom({ emoji: e.target.value })} className="bg-black/30 border-white/10 mt-1 text-center text-lg" />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Nom</Label>
                  <Input value={custom.name} maxLength={60} placeholder="Ex : 2 ans de Vanta Pulse" onChange={(e) => setCustom({ name: e.target.value })} className="bg-black/30 border-white/10 mt-1" data-testid="season-custom-name" />
                </div>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Date et heure</Label>
                <Input type="datetime-local" value={custom.date && custom.date.length === 10 ? `${custom.date}T00:00` : custom.date} onChange={(e) => setCustom({ date: e.target.value.slice(0, 16) })} className="bg-black/30 border-white/10 mt-1" data-testid="season-custom-date" />
              </div>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={Boolean(custom.yearly)} onChange={(e) => setCustom({ yearly: e.target.checked })} className="accent-blue-500 w-4 h-4" />
                Revient chaque année
              </label>
              <div>
                <Label className="text-white/60 text-xs">Compte à rebours affiché (jours avant, en automatique)</Label>
                <Input type="number" min={0} max={366} value={custom.before} onChange={(e) => setCustom({ before: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Durée de l'événement (jours)</Label>
                <Input type="number" min={1} max={366} value={custom.span} onChange={(e) => setCustom({ span: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Texte sous le titre</Label>
                <Input value={custom.label} maxLength={80} placeholder={`avant ${custom.name || "l'événement"}`} onChange={(e) => setCustom({ label: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Message le jour J</Label>
                <Input value={custom.live} maxLength={200} placeholder="Ex : Merci d'être là depuis 2 ans !" onChange={(e) => setCustom({ live: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <Label className="text-white/60 text-xs">Couleurs</Label>
                  <div className="flex gap-2 mt-1">
                    <input type="color" value={custom.color} onChange={(e) => setCustom({ color: e.target.value })} className="w-10 h-9 rounded bg-transparent border border-white/10" aria-label="Couleur 1" />
                    <input type="color" value={custom.color2} onChange={(e) => setCustom({ color2: e.target.value })} className="w-10 h-9 rounded bg-transparent border border-white/10" aria-label="Couleur 2" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Effet animé</Label>
                <select value={custom.effect} onChange={(e) => setCustom({ effect: e.target.value })} className="w-full mt-1 h-9 rounded-md bg-black/30 border border-white/10 px-2 text-sm">
                  {Object.entries(SEASON_EFFECTS).map(([id, fx]) => <option key={id} value={id} className="bg-[#12121a]">{fx.label}</option>)}
                </select>
              </div>
              {draft.custom && (
                <div className="sm:col-span-2">
                  <Button type="button" variant="ghost" className="h-7 text-xs text-red-400" onClick={() => set({ custom: null, event: draft.event === CUSTOM_EVENT_ID ? "noel" : draft.event })}>Effacer l'événement personnalisé</Button>
                </div>
              )}
            </div>
          </details>

          {/* Options */}
          <div className="bg-[#12121a] border border-white/5 rounded-lg p-5">
            <h3 className="font-semibold mb-1">Affichage</h3>
            <div className="divide-y divide-white/5">
              <Toggle on={draft.banner !== false} onChange={(v) => set({ banner: v })} label="Carte de l'événement" hint="En bas à gauche de toutes les pages (le visiteur peut la réduire)." testId="season-toggle-banner" />
              <Toggle on={draft.countdown !== false} onChange={(v) => set({ countdown: v })} label="Compte à rebours" hint="Jours, heures, minutes et secondes jusqu'à la fête, en direct." testId="season-toggle-countdown" />
              <Toggle on={draft.effects !== false} onChange={(v) => set({ effects: v })} label="Effets animés" hint="Neige, citrouilles, cœurs, confettis… Le visiteur peut les couper, et ils sont coupés pour ceux qui réduisent les animations." testId="season-toggle-effects" />
              <Toggle on={draft.theme !== false} onChange={(v) => set({ theme: v })} label="Couleurs de la fête" hint="Liseré en haut de l'écran et lueur aux couleurs de l'événement." testId="season-toggle-theme" />
              <Toggle on={draft.eggs !== false} onChange={(v) => set({ eggs: v })} label="Surprise cachée (easter egg)" hint="Un petit objet discret sur les pages : la citrouille d'Halloween fait apparaître des yeux rouges avec un rire, le cadeau de Noël fait passer le traîneau, le champagne du Nouvel An saute…" testId="season-toggle-eggs" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div>
                <Label className="text-white/60 text-xs">Heure du compte à rebours</Label>
                <select value={tz} onChange={(e) => set({ timezone: e.target.value })} className="w-full mt-1 h-9 rounded-md bg-black/30 border border-white/10 px-2 text-sm" data-testid="season-timezone">
                  {Object.entries(SEASON_TIMEZONES).map(([id, label]) => <option key={id} value={id} className="bg-[#12121a]">{label}</option>)}
                </select>
                <p className="text-white/35 text-[11px] mt-1 leading-snug">
                  {tz === "visitor" ? "Chacun voit minuit chez lui : un visiteur au Québec fête le Nouvel An 6 h après la France." : "Tout le monde arrive à zéro en même temps, à l'heure de Paris (heure d'été et d'hiver comprises)."}
                </p>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Message (optionnel)</Label>
                <textarea value={draft.message || ""} maxLength={200} rows={2} onChange={(e) => set({ message: e.target.value })} placeholder="Ex : -20 % sur tous les bots avec le code NOEL" className="w-full bg-black/30 border border-white/10 rounded-md mt-1 p-2 text-sm text-white resize-none focus:outline-none focus:border-blue-500/40" data-testid="season-message" />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={save} disabled={saving || !dirty} className="bg-blue-600 hover:bg-blue-500 rounded-lg text-sm" data-testid="season-save">{saving ? "Enregistrement…" : "Enregistrer pour tout le site"}</Button>
            {dirty && <Button type="button" variant="ghost" className="text-sm" onClick={() => setDraft(saved)}>Annuler les changements</Button>}
            {dirty && <span className="text-amber-300/80 text-xs">Modifications non enregistrées</span>}
          </div>
        </div>

        {/* Aperçu + calendrier */}
        <div className="space-y-6">
          <div className="bg-[#12121a] border border-white/5 rounded-lg p-5 xl:sticky xl:top-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Eye className="w-4 h-4 text-white/50" />Aperçu</h3>
            {preview ? (
              draft.banner !== false ? <SeasonCard season={preview} cfg={payload} now={now} inline /> : <p className="text-white/40 text-sm">Carte masquée : seuls les couleurs et les effets s'affichent.</p>
            ) : (
              <p className="text-white/40 text-sm">{draft.mode === "off" ? "Événements désactivés." : draft.mode === "auto" ? "Aucune fête cochée n'est proche en ce moment : rien ne s'affiche." : "Choisis un événement."}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              {previewing ? (
                <Button type="button" variant="outline" className="border-white/10 text-sm h-8" onClick={stopPreview}><EyeOff className="w-3.5 h-3.5 mr-1.5" />Arrêter l'aperçu</Button>
              ) : (
                <Button type="button" variant="outline" className="border-white/10 text-sm h-8" onClick={startPreview} disabled={!preview} data-testid="season-preview">
                  <Eye className="w-3.5 h-3.5 mr-1.5" />Voir sur la page
                </Button>
              )}
              <Button type="button" variant="outline" className="border-white/10 text-sm h-8" onClick={testCelebration} disabled={!preview} title="Feux d'artifice (sur le site, seulement sur l'accueil)"><PartyPopper className="w-3.5 h-3.5 mr-1.5" />Tester le passage à zéro</Button>
              <Button type="button" variant="outline" className="border-white/10 text-sm h-8" onClick={testEgg} disabled={!preview || draft.eggs === false} data-testid="season-test-egg"><Gift className="w-3.5 h-3.5 mr-1.5" />Tester la surprise</Button>
            </div>
          </div>

          <div className="bg-[#12121a] border border-white/5 rounded-lg p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><CalendarHeart className="w-4 h-4 text-pink-400" />Prochaines fêtes</h3>
            <ul className="space-y-2">
              {upcoming.map(({ ev, occ }) => {
                const days = Math.ceil((occ.start - now) / 86400000);
                const on = draft.mode === "auto" ? autoSet.has(ev.id) : draft.mode === "manual" && draft.event === ev.id;
                return (
                  <li key={`${ev.id}-${occ.start}`} className={`flex items-center gap-2.5 text-sm ${on ? "" : "opacity-50"}`}>
                    <span className="w-6 text-center">{ev.emoji}</span>
                    <span className="flex-1 min-w-0 truncate">{ev.name}</span>
                    <span className="text-white/40 text-xs shrink-0">{days <= 0 ? "en cours" : days === 1 ? "demain" : `dans ${days} j`}</span>
                  </li>
                );
              })}
            </ul>
            {draft.mode === "manual" && findSeasonEvent(draft.event, payload.custom) === null && (
              <p className="text-amber-300/80 text-xs mt-3">Cet événement n'existe plus : choisis-en un autre.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
